<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import type { FieldPartTemplate } from "@/types/field-observations.types";
import { useGetFieldPartTemplates } from "@/stores/queries/field-observations.query";
import FieldPartTemplateTreePreview from "./FieldPartTemplateTreePreview.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

/**
 * Choix d'un modèle d'arborescence + aperçu. Partagé par les deux enveloppes :
 * la feuille du bas de l'outil terrain et la modal de l'outil dessin — seule la
 * coquille change, la sélection reste ici.
 */

const modelValue = defineModel<number | null>({ default: null });

const { t } = useTranslation();

const { data: templates, isLoading } = useGetFieldPartTemplates();

const isEmpty = computed(() => !templates.value?.length);

const selectedTemplate = computed<FieldPartTemplate | undefined>(() =>
  templates.value?.find((template) => template.id === modelValue.value)
);
</script>

<template>
  <div class="template-picker">
    <div v-if="isLoading" class="picker-state">
      <span class="spinner-border spinner-border-sm me-2" />
      {{ t("common:loading") }}
    </div>

    <div v-else-if="isEmpty" class="picker-state">
      <PhosphorIcon name="folder-open" :size="32" class="state-icon" />
      <p class="mb-0">
        {{ t("dashboard:fieldObservations.partTemplates.empty") }}
      </p>
    </div>

    <template v-else>
      <div class="templates-list">
        <div
          v-for="template in templates"
          :key="template.id"
          class="template-option"
          :class="{ selected: modelValue === template.id }"
          role="button"
          :title="template.description"
          @click="modelValue = template.id"
        >
          <span class="template-radio">
            <span v-if="modelValue === template.id" class="radio-dot" />
          </span>
          <span class="template-name">{{ template.name }}</span>
          <span v-if="template.description" class="template-description">
            {{ template.description }}
          </span>
        </div>
      </div>

      <div v-if="selectedTemplate" class="preview-section">
        <div class="preview-label">
          <PhosphorIcon name="eye" :size="16" class="me-1" />
          {{ t("dashboard:fieldObservations.partTemplates.saveModal.preview") }}
        </div>
        <div class="preview-tree">
          <FieldPartTemplateTreePreview :nodes="selectedTemplate.tree.parts" />
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.template-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.picker-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--bs-secondary-color);
  text-align: center;
  font-size: 14px;
}

.state-icon {
  margin-bottom: 0.75rem;
  opacity: 0.5;
}

.templates-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 200px;
  overflow-y: auto;
}

/* Une ligne par modèle : radio + nom, la description suit en gris si la place
   le permet. C'est une liste de choix, pas une galerie de cartes. */
.template-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    background-color: var(--bs-tertiary-bg);
  }

  &.selected {
    border-color: var(--bs-primary);
    background-color: rgba(var(--bs-primary-rgb), 0.06);
  }
}

.template-radio {
  width: 14px;
  height: 14px;
  border: 1px solid var(--bs-border-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .selected & {
    border-color: var(--bs-primary);
  }
}

.radio-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: var(--bs-primary);
}

.template-name {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.template-description {
  font-size: 12px;
  color: var(--bs-secondary-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.preview-section {
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  overflow: hidden;
}

.preview-label {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: var(--bs-tertiary-bg);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
}

.preview-tree {
  padding: 12px;
  max-height: 200px;
  overflow-y: auto;
}
</style>
